/**
 * A question rendered the way the student sees it — same markdown, same math,
 * same option layout, same 16px reading measure. The review queue depends on
 * this being faithful: approving a question that renders differently in the
 * exam is the failure mode this screen exists to prevent.
 *
 * `answer` overlays grading marks (correct key, the student's choice). Omit it
 * for a plain preview.
 */
import type {
  AnswerResponse,
  CorrectAnswer,
  QuestionWithContext,
} from "@exam/shared";
import { Check, Minus, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { QuestionBody } from "@/lib/markdown";
import { QuestionFigure } from "./QuestionFigure";
import { formatMarks } from "../lib/format";

export interface AnswerOverlay {
  response: AnswerResponse | null;
  is_correct: boolean | null;
}

function correctKeys(answer: CorrectAnswer): Set<string> {
  if (answer.type === "MCQ") return new Set([answer.key]);
  if (answer.type === "MSQ") return new Set(answer.keys);
  return new Set();
}

function chosenKeys(response: AnswerResponse | null): Set<string> {
  if (!response) return new Set();
  if (response.type === "MCQ")
    return new Set(response.key ? [response.key] : []);
  if (response.type === "MSQ") return new Set(response.keys);
  return new Set();
}

/** The plain-language form of a correct answer, for the NAT case and summaries. */
export function describeAnswer(answer: CorrectAnswer): string {
  if (answer.type === "MCQ") return answer.key.toUpperCase();
  if (answer.type === "MSQ")
    return answer.keys.map((k) => k.toUpperCase()).join(", ");
  // A CODE question has no key: the tests are the key, and they live on the
  // question rather than in this column.
  if (answer.type === "CODE") return "tests";
  return answer.tolerance > 0
    ? `${formatMarks(answer.value)} ± ${formatMarks(answer.tolerance)}`
    : formatMarks(answer.value);
}

/** The student's response as a short string, for dense tables. */
export function describeResponse(response: AnswerResponse | null): string {
  if (!response) return "—";
  if (response.type === "MCQ")
    return response.key ? response.key.toUpperCase() : "—";
  if (response.type === "MSQ")
    return response.keys.length === 0
      ? "—"
      : response.keys.map((k) => k.toUpperCase()).join(", ");
  // For code, the useful one-line summary is how much of it worked — the
  // source itself is far too long for a dense table.
  if (response.type === "CODE") {
    return response.total === 0
      ? "not run"
      : `${response.passed}/${response.total} tests`;
  }
  return response.value === null ? "—" : formatMarks(response.value);
}

/**
 * What the student actually wrote, for a CODE question.
 *
 * The submitted program was stored from the first day CODE questions existed
 * — `AnswerResponse` keeps the source precisely so the pass counts, which are
 * produced on the student's own machine, can be checked against something —
 * but nothing ever displayed it. A reviewer saw "2/6 tests" and had no way to
 * see the attempt, which is the only thing worth reading when a student is
 * asking why they lost the marks.
 */
function CodeAnswer({ response }: { response: AnswerResponse | null }) {
  if (response === null || response.type !== "CODE") {
    return (
      <div className="rounded-md border border-border bg-surface-sunken px-3 py-2">
        <p className="text-2xs text-ink-faint">Code answer</p>
        <p className="mt-0.5 text-sm text-ink-faint">
          Nothing was submitted for this question.
        </p>
      </div>
    );
  }

  const failed = response.results.filter((r) => !r.passed);

  return (
    <div className="space-y-2 rounded-md border border-border bg-surface-sunken p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-2xs font-medium text-ink-muted">
          Submitted code
        </span>
        <span
          className={cn(
            "tabular text-2xs font-medium",
            response.total > 0 && response.passed === response.total
              ? "text-answered"
              : response.passed > 0
                ? "text-review"
                : "text-violation",
          )}
        >
          {response.total === 0
            ? "never run"
            : `${response.passed}/${response.total} tests passed`}
        </span>
      </div>

      {/* Whitespace preserved and horizontally scrollable: reformatting a
          student's code, or wrapping it, changes what they are being asked
          about. */}
      <pre className="max-h-96 overflow-auto rounded-sm border border-border bg-surface p-2 text-2xs leading-relaxed text-ink">
        {/* Optional-chained on purpose: this column is returned straight from
            the database without re-parsing, so one hand-edited row without a
            source would otherwise throw and blank the whole review page. */}
        <code>{response.source?.trim() || "(empty)"}</code>
      </pre>

      {response.results.length > 0 ? (
        <ul className="space-y-1">
          {response.results.map((result, i) => (
            <li
              key={`${result.name}-${i}`}
              className="flex items-start gap-1.5 text-2xs"
            >
              {result.passed ? (
                <Check className="mt-0.5 size-3 shrink-0 text-answered" />
              ) : (
                <X className="mt-0.5 size-3 shrink-0 text-violation" />
              )}
              <span className="min-w-0 flex-1 text-ink-muted">
                {result.name}
                {result.detail ? (
                  <span className="block text-ink-faint">{result.detail}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* The counts came off the student's machine; the source is the only
          thing here that can be re-checked. */}
      {failed.length > 0 ? (
        <p className="text-2xs text-ink-faint">
          Run the code above against the question&rsquo;s tests to confirm this
          result.
        </p>
      ) : null}
    </div>
  );
}

/**
 * A CODE question with no attempt attached — the builder, the bank, the review
 * queue.
 *
 * Those screens showed the numeric-answer box, which is wrong twice over: it
 * says "Numerical answer" about a programming question, and it hides the two
 * things a reviewer has to read to approve one. The tests ARE the answer key
 * here, so a CODE question without them on screen cannot be reviewed at all.
 */
function CodeBrief({ question }: { question: QuestionWithContext }) {
  return (
    <div className="space-y-2 rounded-md border border-border bg-surface-sunken p-3">
      <p className="text-2xs font-medium text-ink-muted">
        Code question — the student writes a program here
      </p>

      {question.starter_code ? (
        <div>
          <p className="text-2xs text-ink-faint">Starter code</p>
          <pre className="mt-1 max-h-60 overflow-auto rounded-sm border border-border bg-surface p-2 text-2xs leading-relaxed text-ink">
            <code>{question.starter_code}</code>
          </pre>
        </div>
      ) : null}

      {question.tests && question.tests.length > 0 ? (
        <div>
          <p className="text-2xs text-ink-faint">
            Tests ({question.tests.length})
          </p>
          <ul className="mt-1 space-y-0.5">
            {question.tests.map((test, i) => (
              <li
                key={`${test.name}-${i}`}
                className="tabular text-2xs text-ink-muted"
              >
                {test.call} → {JSON.stringify(test.expect)}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-2xs text-violation">
          This question has no tests, so it can never be marked.
        </p>
      )}
    </div>
  );
}

export function QuestionView({
  question,
  showAnswer = false,
  answer,
  className,
  onFigureRefreshed,
}: {
  question: QuestionWithContext;
  /** Reveals the correct option and the solution. */
  showAnswer?: boolean;
  answer?: AnswerOverlay;
  className?: string;
  /** Signed figure URLs expire; this keeps the caller's copy in step. */
  onFigureRefreshed?: (question: QuestionWithContext) => void;
}) {
  const correct = correctKeys(question.correct_answer);
  const chosen = chosenKeys(answer?.response ?? null);

  return (
    <div className={cn("space-y-4", className)}>
      <QuestionBody source={question.body_md} />

      {/*
       * A question that references a figure is only answerable with it, so it
       * sits inline with the body rather than in an attachment list — and when
       * it is missing, that is stated rather than implied by absence.
       */}
      <QuestionFigure question={question} onRefreshed={onFigureRefreshed} />

      {question.type === "CODE" ? (
        answer ? (
          <CodeAnswer response={answer.response} />
        ) : (
          <CodeBrief question={question} />
        )
      ) : question.options.length > 0 ? (
        <ul className="space-y-1.5">
          {question.options.map((option) => {
            const isCorrect = showAnswer && correct.has(option.key);
            const isChosen = chosen.has(option.key);
            const isWrongChoice =
              showAnswer && isChosen && !correct.has(option.key);

            return (
              <li
                key={option.key}
                className={cn(
                  "flex items-start gap-2.5 rounded-md border px-3 py-2",
                  isCorrect
                    ? "border-answered/30 bg-answered-soft"
                    : isWrongChoice
                      ? "border-violation/30 bg-violation-soft"
                      : "border-border bg-surface",
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-sm border text-2xs font-medium",
                    // MSQ is a checkbox in the exam UI; MCQ is a radio.
                    question.type === "MSQ" ? "rounded-sm" : "rounded-full",
                    isCorrect
                      ? "border-answered text-answered"
                      : isWrongChoice
                        ? "border-violation text-violation"
                        : "border-border-strong text-ink-faint",
                  )}
                >
                  {option.key.toUpperCase()}
                </span>

                <div className="min-w-0 flex-1 pt-0.5">
                  <QuestionBody source={option.text} />
                </div>

                <div className="flex shrink-0 items-center gap-1.5 pt-0.5">
                  {isChosen ? (
                    <span className="text-2xs text-ink-muted">Chose</span>
                  ) : null}
                  {isCorrect ? (
                    <Check
                      className="size-3.5 text-answered"
                      aria-label="Correct answer"
                    />
                  ) : isWrongChoice ? (
                    <X
                      className="size-3.5 text-violation"
                      aria-label="Selected, incorrect"
                    />
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="rounded-md border border-border bg-surface-sunken px-3 py-2">
          <p className="text-2xs text-ink-faint">Numerical answer</p>
          {answer ? (
            <p className="tabular mt-0.5 text-sm text-ink">
              {describeResponse(answer.response)}
              {answer.is_correct === false ? (
                <X className="ml-1.5 inline size-3.5 text-violation" />
              ) : answer.is_correct === true ? (
                <Check className="ml-1.5 inline size-3.5 text-answered" />
              ) : (
                <Minus className="ml-1.5 inline size-3.5 text-ink-faint" />
              )}
            </p>
          ) : (
            <p className="mt-0.5 text-sm text-ink-faint">
              The student types a number here.
            </p>
          )}
        </div>
      )}

      {showAnswer ? (
        <div className="space-y-3 rounded-md border border-border bg-surface-sunken p-3">
          <div className="flex items-baseline gap-2">
            <span className="text-2xs font-medium text-ink-muted">
              Correct answer
            </span>
            <span className="tabular text-sm font-medium text-answered">
              {describeAnswer(question.correct_answer)}
            </span>
          </div>

          {question.solution_md ? (
            <div>
              <p className="mb-1 text-2xs font-medium text-ink-muted">
                Solution
              </p>
              <QuestionBody source={question.solution_md} size="ui" />
            </div>
          ) : (
            <p className="text-xs text-ink-faint">No solution written.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
